import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errMsg } from '../../lib/api';
import { getToken } from '../../lib/auth';
import AdminProducts from './AdminProducts';
import AdminHeroes from './AdminHeroes';
import AdminData from './AdminData';
import AdminTemplates from './AdminTemplates';
import AdminSettings from './AdminSettings';
import AdminNotary from './AdminNotary';
import AdminUsers from './AdminUsers';
import AdminGuide from './AdminGuide';
import AdminCategories from './AdminCategories';
import AdminPhrases from './AdminPhrases';
import AdminSeo from './AdminSeo';
import AdminModeration from './AdminModeration';
import { Icon } from '../../lib/icons';

type Tab = 'dashboard' | 'users' | 'products' | 'heroes' | 'templates' | 'settings' | 'notary' | 'data' | 'categories' | 'guide' | 'phrases' | 'seo' | 'moderation';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'dashboard', label: '数据概览', icon: 'chart' },
  { id: 'users', label: '用户管理', icon: 'user' },
  { id: 'products', label: '道具管理', icon: 'candle' },
  { id: 'heroes', label: '英雄纪念堂', icon: 'building' },
  { id: 'templates', label: '页面模板', icon: 'palette' },
  { id: 'settings', label: '上传额度', icon: 'gear' },
  { id: 'notary', label: '链上存证', icon: 'chain' },
  { id: 'data', label: '纪念馆与订单', icon: 'list' },
  { id: 'categories', label: '系统设置', icon: 'tags' },
  { id: 'guide', label: '道具 SVG 规范', icon: 'code' },
  { id: 'phrases', label: '追思常用语', icon: 'card' },
  { id: 'seo', label: 'AI SEO 栏目', icon: 'globe' },
  { id: 'moderation', label: '内容审核', icon: 'shield' },
];

interface Stats {
  users: number;
  memorials: number;
  heroes: number;
  products: number;
  paidOrders: number;
  pendingOrders: number;
  revenueCents: number;
}

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('dashboard');
  const [role, setRole] = useState<string | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!getToken()) {
      navigate('/login');
      return;
    }

    api<{ user: { role: string } }>('/auth/me').then((res) => {
      if (!res.ok) {
        navigate('/login');
        return;
      }
      const r = res.data?.user?.role ?? 'user';
      setRole(r);

      if (r === 'admin') {
        api<Stats>('/admin/dashboard').then((s) => {
          if (s.ok && s.data) setStats(s.data);
          else setError(errMsg(s, '加载统计失败'));
        });
      }
      setChecking(false);
    });
  }, [navigate]);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50">
        <p className="text-sm text-ink-500">验证权限中…</p>
      </div>
    );
  }

  if (role !== 'admin') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50 px-6">
        <div className="max-w-sm rounded-2xl border border-ink-200 bg-white p-8 text-center">
          <p className="text-4xl"><Icon name="lock" /></p>
          <h1 className="mt-4 font-serifcn text-lg text-ink-900">需要管理员权限</h1>
          <p className="mt-2 text-xs text-ink-500">
            当前账号不是管理员。请使用管理员账号登录后再访问后台。
          </p>
          <button
            onClick={() => navigate('/login')}
            className="mt-5 h-10 w-full rounded-xl bg-ink-900 text-sm text-ink-50"
          >
            去登录
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-50">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-ink-900 text-sm text-candle-300">
              墓
            </span>
            <div>
              <h1 className="text-sm font-medium text-ink-900">线上墓迪 · 管理后台</h1>
              <p className="text-[11px] text-ink-500">内容运营与数据监控</p>
            </div>
          </div>
          <a
            href="/"
            className="text-xs text-ink-600 underline"
          >
            返回前台
          </a>
        </div>

        <nav className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-6">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm transition ${
                tab === t.id
                  ? 'border-ink-900 text-ink-900'
                  : 'border-transparent text-ink-500 hover:text-ink-800'
              }`}
            >
              <Icon name={t.icon} size={15} className="mr-1.5 align-[-2px]" />
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        {error && (
          <p className="mb-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>
        )}

        {tab === 'dashboard' && <Dashboard stats={stats} />}
        {tab === 'users' && <AdminUsers />}
        {tab === 'products' && <AdminProducts />}
        {tab === 'heroes' && <AdminHeroes />}
        {tab === 'templates' && <AdminTemplates />}
        {tab === 'phrases' && <AdminPhrases />}
        {tab === 'seo' && <AdminSeo />}
        {tab === 'moderation' && <AdminModeration />}
        {tab === 'settings' && <AdminSettings />}
        {tab === 'notary' && <AdminNotary />}
        {tab === 'data' && <AdminData />}
        {tab === 'categories' && <AdminCategories />}
        {tab === 'guide' && <AdminGuide />}
      </main>
    </div>
  );
}

function Dashboard({ stats }: { stats: Stats | null }) {
  if (!stats) return <p className="text-sm text-ink-500">加载中…</p>;

  const cards = [
    { label: '注册用户', value: stats.users, unit: '人' },
    { label: '亲友纪念馆', value: stats.memorials, unit: '座' },
    { label: '英雄纪念堂', value: stats.heroes, unit: '座' },
    { label: '道具总数', value: stats.products, unit: '个' },
    { label: '已支付订单', value: stats.paidOrders, unit: '笔' },
    { label: '待支付订单', value: stats.pendingOrders, unit: '笔' },
  ];

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <div
            key={c.label}
            className="rounded-2xl border border-ink-200 bg-white p-5"
          >
            <p className="text-xs text-ink-500">{c.label}</p>
            <p className="mt-2 font-serifcn text-3xl text-ink-900">
              {c.value}
              <span className="ml-1 text-sm text-ink-400">{c.unit}</span>
            </p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-candle-400/40 bg-candle-300/10 p-6">
        <p className="text-xs text-ink-600">累计收入（模拟支付）</p>
        <p className="mt-2 font-serifcn text-3xl text-candle-600">
          ${(stats.revenueCents / 100).toFixed(2)}
        </p>
        <p className="mt-2 text-[11px] text-ink-500">
          当前使用 MockPay，未接入真实支付渠道
        </p>
      </div>
    </div>
  );
}
